import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Navigate } from "react-router-dom"

import {
  OnlineStatusReportDevice,
  OnlineStatusReportPeriod,
  OnlineStatusReportSummary,
  useCreateOnlineStatusReportMutation,
  useLazyGetOnlineStatusReportDevicesQuery,
  useLazyGetOnlineStatusReportSummaryQuery,
} from "store/api/onlineStatusReportApi"
import { FeatureFlag, isFeatureEnabled } from "store/featureFlagsSlice"
import { useAppSelector } from "store/hooks"
import { UITypes } from "types"
import { ReactComponent as IconSearch } from "assets/svg/icon-magnifying-glass.svg"

import useExportCSV from "hooks/useExportCSV"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { ContentWrapper } from "components/common/ContentWrapper"
import { DateRangeTabsFilter } from "components/common/DateRangeTabsFilter"
import {
  getDateRangeParams,
  getFilterValueFromParams,
} from "components/common/DateRangeTabsFilter/dateRangeFilterUtils"
import { ButtonNew } from "components/form/ButtonNew"

import { ReportTable } from "./reportTable"
import { adHocReportHeaderCells } from "./tableConfig"

import styles from "./index.module.scss"

type Period = "week" | "month"

const toReportPeriod = (period: Period): OnlineStatusReportPeriod => (period === "week" ? "last_week" : "last_month")

const getPeriodFromParams = (params: Record<string, string>): Period =>
  getFilterValueFromParams(params, "report_period") === "last-month" ? "month" : "week"

const getReportTableRows = (devices: OnlineStatusReportDevice[], currentPage: number, perPage: number) =>
  devices.map((row, index) => ({
    sn: { value: row.sn },
    customer_email: { value: row.customer_email, csvValue: row.customer_email ?? "N/A" },
    status: { value: row.status },
    online_percentage: { value: row.online_percentage, csvValue: row.online_percentage ?? "N/A" },
    address: { value: row.address, csvValue: row.address ?? "N/A" },
    thermostat_name: { value: row.thermostat_name, csvValue: row.thermostat_name ?? "N/A" },
    rowSettings: { id: (currentPage - 1) * perPage + index, item: row },
  })) as UITypes.TableBodyRow[]

const AdHocReportsPage = () => {
  const isAdHocReportsEnabled = useAppSelector(isFeatureEnabled(FeatureFlag.AdHocReports))
  const { urlQueryParams, setUrlQueryParams } = useUrlQueryParams()
  const [reportId, setReportId] = useState<string | null>(null)
  const [reportState, setReportState] = useState<"empty" | "preparing" | "complete" | "failed">("empty")
  const [createReport, { isLoading: isCreating }] = useCreateOnlineStatusReportMutation()
  const [fetchSummary] = useLazyGetOnlineStatusReportSummaryQuery()
  const [fetchDevices, { data: devicesData, isFetching: isFetchingDevices }] =
    useLazyGetOnlineStatusReportDevicesQuery()
  const [summaryData, setSummaryData] = useState<OnlineStatusReportSummary>()
  const timeoutRef = useRef<number | null>(null)

  const clearPollingTimeout = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!urlQueryParams.report_period_start && !urlQueryParams.report_period_end) {
      setUrlQueryParams(getDateRangeParams("last-week", "report_period"))
    }
  }, [])

  const currentPage = Number(urlQueryParams.page) || 1
  const perPage = Number(urlQueryParams.perPage) || 15
  const orderColumn = urlQueryParams.orderColumn || "sn"
  const orderType = urlQueryParams.orderType === "DESC" ? "DESC" : "ASC"
  const reportPeriod = summaryData?.reporting_period

  const fetchReport = useCallback(
    async (id: string, retryAfterSeconds = 2) => {
      try {
        const summary = await fetchSummary(id).unwrap()
        if (summary.state === "preparing") {
          setReportState("preparing")
          timeoutRef.current = window.setTimeout(
            () => void fetchReport(id, summary.retry_after_seconds ?? retryAfterSeconds),
            (summary.retry_after_seconds ?? retryAfterSeconds) * 1000,
          )
          return
        }

        setSummaryData(summary)
        setReportState("complete")
      } catch {
        setReportState("failed")
      }
    },
    [fetchSummary],
  )

  useEffect(() => clearPollingTimeout, [clearPollingTimeout])

  const handleGenerate = async () => {
    clearPollingTimeout()
    setReportState("preparing")
    setSummaryData(undefined)

    try {
      const response = await createReport({ period: toReportPeriod(getPeriodFromParams(urlQueryParams)) }).unwrap()
      setReportId(response.report_id)

      if (response.state === "complete") {
        const summary = await fetchSummary(response.report_id).unwrap()
        setSummaryData(summary)
        setReportState("complete")
      } else {
        await fetchReport(response.report_id, response.retry_after_seconds ?? 2)
      }
    } catch {
      setReportState("failed")
    }
  }

  useEffect(() => {
    if (!reportId || reportState !== "complete") return

    void fetchDevices({
      reportId,
      params: {
        sn: urlQueryParams.sn,
        customer_email: urlQueryParams.customer_email,
        address: urlQueryParams.address,
        thermostat_name: urlQueryParams.thermostat_name,
        order_column: orderColumn,
        order_type: orderType,
        page: currentPage,
        per_page: perPage,
      },
    })
  }, [currentPage, fetchDevices, orderColumn, orderType, perPage, reportId, reportState, urlQueryParams])

  const tableRows = useMemo(
    () => getReportTableRows(devicesData?.data ?? [], currentPage, perPage),
    [currentPage, devicesData?.data, perPage],
  )

  const { handleLocalExport } = useExportCSV({
    params: {},
    headerCells: adHocReportHeaderCells,
    fileName: "thermostat_online_status_report",
  })

  if (!isAdHocReportsEnabled) return <Navigate to="/" replace />

  const isPreparing = isCreating || reportState === "preparing"
  const hasReport = reportState === "complete" && !!devicesData

  return (
    <>
      <div className={styles.headerCard}>
        <h1 className={styles.title}>Thermostat online status report</h1>
        <p className={styles.subtitle}>View online percentages for thermostats assigned to your account.</p>
        <div className={styles.controls}>
          <DateRangeTabsFilter
            paramName="report_period"
            urlQueryParams={urlQueryParams}
            onParamsChange={setUrlQueryParams}
            filterGroups={[["last-week"], ["last-month"]]}
          />
          <ButtonNew color="blue" onClick={handleGenerate} disabled={isPreparing}>
            Generate report
          </ButtonNew>
        </div>
      </div>
      <ContentWrapper title="Reports">
        <div className={styles.reportCard}>
          {isPreparing && (
            <div className={styles.generating} role="status">
              <div className={styles.generatingMessage}>
                <IconSearch />
                Please wait while the report is getting generated
              </div>
            </div>
          )}
          {reportState === "failed" && (
            <div className={styles.errorMessage}>We couldn&apos;t generate this report. Please try again.</div>
          )}
          {!isPreparing && reportState === "empty" && (
            <div className={styles.empty}>
              <IconSearch />
              <strong>No report yet</strong>
              <span>
                Pick a date range above and choose Generate report to see online percentages for your installed
                thermostats.
              </span>
            </div>
          )}
          {!isPreparing && hasReport && (
            <ReportTable
              rows={tableRows}
              meta={devicesData.meta}
              isLoading={isFetchingDevices}
              onExport={() => handleLocalExport(tableRows)}
              canExport={Boolean(devicesData?.data.length)}
              periodLabel={reportPeriod?.display_label}
              averageOnlinePercentage={summaryData?.overall_online_percentage}
            />
          )}
        </div>
      </ContentWrapper>
    </>
  )
}

export default AdHocReportsPage
